import { Component, OnInit } from '@angular/core';
import {
  Product,
  ProductSquashExport,
  ProductSquashPreviewResponse,
  ProductSquashSession,
  ProductsService,
  SquashPrimaryGroup,
  SquashCloseMatch,
  SquashVariantSummary,
} from 'src/app/services/products.service';

const STORAGE_SESSION = 'productSquash.session';
/** Bump when squash preview variant payload shape changes. */
const PREVIEW_SCHEMA_VERSION = 4;

@Component({
  selector: 'app-product-squash',
  templateUrl: './product-squash.component.html',
  styleUrls: ['./product-squash.component.scss'],
})
export class ProductSquashComponent implements OnInit {
  allProducts: Product[] = [];
  displayPrimaryProducts: Product[] = [];
  displayChildProducts: Product[] = [];

  groups: SquashPrimaryGroup[] = [];
  activePrimaryId: string | null = null;

  primarySearchText = '';
  childSearchText = '';
  hideAlreadySelected = false;
  loadingProducts = false;
  loadingPreview = false;
  error = '';
  previewNeedsRefresh = false;

  constructor(private productsService: ProductsService) {}

  ngOnInit() {
    this.restoreSession();
    this.loadAllProducts();
  }

  get activeGroup(): SquashPrimaryGroup | null {
    if (!this.activePrimaryId) return null;
    return this.groups.find((g) => g.primary_product.id === this.activePrimaryId) ?? null;
  }

  get activePrimary(): Product | null {
    return this.activeGroup?.primary_product ?? null;
  }

  get activeChildIds(): Set<string> {
    return new Set(this.activeGroup?.child_product_ids ?? []);
  }

  get activePreview(): ProductSquashPreviewResponse | null {
    return this.activeGroup?.preview ?? null;
  }

  get savedGroups(): SquashPrimaryGroup[] {
    return this.groups;
  }

  restoreSession() {
    try {
      const sessionJson = localStorage.getItem(STORAGE_SESSION);
      if (sessionJson) {
        const session = JSON.parse(sessionJson) as ProductSquashSession;
        this.groups = session.groups ?? [];
        this.activePrimaryId = session.active_primary_id ?? null;
        this.hideAlreadySelected = session.hide_already_selected ?? false;
        this.migrateStalePreviews(session.preview_schema_version);
        return;
      }

      this.migrateLegacySession();
    } catch (err) {
      console.error(err);
    }
  }

  migrateLegacySession() {
    const legacyPrimary = localStorage.getItem('productSquash.primaryProductSnapshot')
      ?? localStorage.getItem('productSquash.primaryProductId');
    if (!legacyPrimary) return;

    let primaryProduct: Product;
    try {
      primaryProduct = legacyPrimary.startsWith('{')
        ? JSON.parse(legacyPrimary)
        : ({ id: legacyPrimary } as Product);
    } catch {
      return;
    }

    const childrenJson = localStorage.getItem('productSquash.childProductIds');
    const previewJson = localStorage.getItem('productSquash.lastPreview');
    const childIds: string[] = childrenJson ? JSON.parse(childrenJson) : [];
    const preview = previewJson
      ? (JSON.parse(previewJson) as ProductSquashPreviewResponse)
      : null;

    this.groups = [
      {
        primary_product: primaryProduct,
        child_product_ids: childIds,
        preview,
      },
    ];
    this.activePrimaryId = primaryProduct.id;
    this.migrateStalePreviews();
    this.persistSession();
  }

  private previewVariantPayloadComplete(
    preview: ProductSquashPreviewResponse
  ): boolean {
    const variants = [
      ...preview.variant_migrations.flatMap((m) => [
        m.from_variant,
        m.to_variant,
      ]),
      ...preview.unmatched_child_variants.map((u) => u.variant),
      ...preview.unmatched_child_variants.flatMap((u) =>
        (u.close_matches ?? []).map((c) => c.primary_variant)
      ),
    ];
    if (variants.length === 0) {
      return true;
    }
    const hasVariantFields = variants.every(
      (v) => v.price_lock_level != null
    );
    const hasCloseMatches = preview.unmatched_child_variants.every((u) =>
      Array.isArray(u.close_matches)
    );
    const hasActiveLicenseCounts = preview.unmatched_child_variants.every(
      (u) => u.active_license_count != null
    );
    return hasVariantFields && hasCloseMatches && hasActiveLicenseCounts;
  }

  private migrateStalePreviews(savedSchemaVersion?: number) {
    const schemaStale = (savedSchemaVersion ?? 1) < PREVIEW_SCHEMA_VERSION;
    let cleared = false;

    for (const group of this.groups) {
      if (
        group.preview &&
        (schemaStale || !this.previewVariantPayloadComplete(group.preview))
      ) {
        group.preview = null;
        cleared = true;
      }
    }

    if (cleared) {
      this.previewNeedsRefresh = true;
      this.persistSession();
    }
  }

  persistSession() {
    const session: ProductSquashSession = {
      groups: this.groups,
      active_primary_id: this.activePrimaryId,
      hide_already_selected: this.hideAlreadySelected,
      preview_schema_version: PREVIEW_SCHEMA_VERSION,
    };
    localStorage.setItem(STORAGE_SESSION, JSON.stringify(session));
  }

  onHideAlreadySelectedChange() {
    this.persistSession();
    this.updatePrimaryDisplayProducts();
    this.updateChildDisplayProducts();
  }

  isChildOfAnyPrimary(productId: string): boolean {
    return this.groups.some((g) => g.child_product_ids.includes(productId));
  }

  isAlreadySelected(productId: string): boolean {
    return this.isSavedPrimary(productId) || this.isChildOfAnyPrimary(productId);
  }

  childParentLabels(productId: string): string {
    return this.groups
      .filter((g) => g.child_product_ids.includes(productId))
      .map((g) => g.primary_product.code)
      .join(', ');
  }

  private matchesProductSearch(product: Product, text: string): boolean {
    if (!text) return true;
    return (
      product.code.toLowerCase().includes(text) ||
      product.title.toLowerCase().includes(text) ||
      product.variance_code.toLowerCase().includes(text) ||
      product.type.toLowerCase().includes(text)
    );
  }

  private shouldHideFromList(productId: string): boolean {
    if (!this.hideAlreadySelected) return false;
    return this.isAlreadySelected(productId);
  }

  resolveGroupsFromCatalog() {
    if (this.allProducts.length === 0) return;

    this.groups = this.groups.map((group) => {
      const found = this.allProducts.find(
        (p) => p.id === group.primary_product.id
      );
      return {
        ...group,
        primary_product: found ?? group.primary_product,
      };
    });

    this.persistSession();
  }

  loadAllProducts() {
    if (this.loadingProducts) return;
    this.loadingProducts = true;
    this.error = '';

    this.productsService.getAllProducts().subscribe({
      next: (res) => {
        this.allProducts = (res?.products ?? []).sort((a, b) => {
          if (a.code < b.code) return -1;
          if (a.code > b.code) return 1;
          if (a.variance_code < b.variance_code) return -1;
          if (a.variance_code > b.variance_code) return 1;
          return 0;
        });

        this.resolveGroupsFromCatalog();
        this.refreshProductLists();
        this.loadingProducts = false;
      },
      error: (err) => {
        this.error = err?.message ?? 'Failed to load products';
        this.loadingProducts = false;
      },
    });
  }

  updatePrimaryDisplayProducts() {
    const text = this.primarySearchText.trim().toLowerCase();
    this.displayPrimaryProducts = this.allProducts.filter((product) => {
      if (this.shouldHideFromList(product.id)) return false;
      return this.matchesProductSearch(product, text);
    });
  }

  updateChildDisplayProducts() {
    const text = this.childSearchText.trim().toLowerCase();

    this.displayChildProducts = this.allProducts.filter((product) => {
      if (this.isSavedPrimary(product.id)) return false;
      if (this.shouldHideFromList(product.id)) return false;
      return this.matchesProductSearch(product, text);
    });
  }

  refreshProductLists() {
    this.updatePrimaryDisplayProducts();
    this.updateChildDisplayProducts();
  }

  isSavedPrimary(productId: string): boolean {
    return this.groups.some((g) => g.primary_product.id === productId);
  }

  isActivePrimary(productId: string): boolean {
    return this.activePrimaryId === productId;
  }

  selectPrimary(product: Product) {
    this.activePrimaryId = product.id;

    const existing = this.groups.find((g) => g.primary_product.id === product.id);
    if (!existing) {
      this.groups.push({
        primary_product: product,
        child_product_ids: [],
        preview: null,
      });
    }

    this.persistSession();
    this.refreshProductLists();
  }

  removePrimaryGroup(primaryId: string, event?: Event) {
    event?.stopPropagation();
    this.groups = this.groups.filter((g) => g.primary_product.id !== primaryId);
    if (this.activePrimaryId === primaryId) {
      this.activePrimaryId = null;
    }
    this.persistSession();
    this.refreshProductLists();
  }

  editPrimaryGroup(primaryId: string) {
    this.activePrimaryId = primaryId;
    this.persistSession();
    this.refreshProductLists();
  }

  getChildProductsForGroup(group: SquashPrimaryGroup): Product[] {
    return group.child_product_ids
      .map((id) => this.allProducts.find((p) => p.id === id))
      .filter((p): p is Product => p != null);
  }

  toggleChild(productId: string, checked: boolean) {
    if (!this.activeGroup) return;

    const childIds = new Set(this.activeGroup.child_product_ids);
    if (checked) {
      childIds.add(productId);
    } else {
      childIds.delete(productId);
    }

    this.activeGroup.child_product_ids = Array.from(childIds);
    this.activeGroup.preview = null;
    this.persistSession();
    this.refreshProductLists();
  }

  removeChildFromActive(productId: string) {
    if (!this.activeGroup) return;
    this.activeGroup.child_product_ids = this.activeGroup.child_product_ids.filter(
      (id) => id !== productId
    );
    this.activeGroup.preview = null;
    this.persistSession();
    this.refreshProductLists();
  }

  isChildSelectedForActive(productId: string): boolean {
    return this.activeChildIds.has(productId);
  }

  getChildProductsForActive(): Product[] {
    if (!this.activeGroup) return [];
    return this.getChildProductsForGroup(this.activeGroup);
  }

  saveCurrentGroup() {
    if (!this.activeGroup) return;
    this.activePrimaryId = null;
    this.persistSession();
    this.refreshProductLists();
  }

  runPreview() {
    if (!this.activeGroup || this.activeGroup.child_product_ids.length === 0) {
      return;
    }

    this.loadingPreview = true;
    this.error = '';

    this.productsService
      .previewSquash({
        primary_product_id: this.activeGroup.primary_product.id,
        child_product_ids: this.activeGroup.child_product_ids,
      })
      .subscribe({
        next: (res) => {
          if (this.activeGroup) {
            if (!this.previewVariantPayloadComplete(res)) {
              this.error =
                'Preview response missing price_lock_level. Restart customer-portal-server with latest code, then refresh preview again.';
              this.loadingPreview = false;
              return;
            }
            this.activeGroup.preview = res;
            this.previewNeedsRefresh = false;
            this.persistSession();
          }
          this.loadingPreview = false;
        },
        error: (err) => {
          this.error = err?.message ?? 'Failed to build squash preview';
          this.loadingPreview = false;
        },
      });
  }

  exportJson() {
    if (this.groups.length === 0) return;

    const exportData: ProductSquashExport = {
      version: 2,
      groups: this.groups.map((group) => ({
        primary_product_id: group.primary_product.id,
        child_product_ids: group.child_product_ids,
        variant_migrations: group.preview?.variant_migrations ?? [],
        unmatched_child_variants:
          group.preview?.unmatched_child_variants ?? [],
      })),
      exported_at: new Date().toISOString(),
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `product-squash-${Date.now()}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  clearSession() {
    this.groups = [];
    this.activePrimaryId = null;
    localStorage.removeItem(STORAGE_SESSION);
    localStorage.removeItem('productSquash.primaryProductId');
    localStorage.removeItem('productSquash.primaryProductSnapshot');
    localStorage.removeItem('productSquash.childProductIds');
    localStorage.removeItem('productSquash.lastPreview');
    this.hideAlreadySelected = false;
    this.refreshProductLists();
  }

  formatPrice(value?: number | null): string {
    if (value == null) return '—';
    return value.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
  }

  variantAttributeLine(
    variant: SquashVariantSummary,
    productId?: string
  ): string {
    const product = productId
      ? this.allProducts.find((p) => p.id === productId)
      : undefined;
    const varianceCode = product?.variance_code;
    const priceLockLevel =
      variant.price_lock_level != null
        ? String(variant.price_lock_level)
        : '—';

    const parts = [
      `${variant.duration_months} mo`,
      variant.delivery_method,
    ];
    if (varianceCode) {
      parts.push(`variance ${varianceCode}`);
    }
    parts.push(`lock ${priceLockLevel}`);
    return parts.join(' · ');
  }

  variantPriceLines(variant: SquashVariantSummary): string[] {
    return [
      `Price: ${this.formatPrice(variant.price)}`,
      `Site: ${this.formatPrice(variant.site_price)}`,
      `Renewal: ${this.formatPrice(variant.renewal_price)}`,
      `Site renewal: ${this.formatPrice(variant.site_renewal_price)}`,
    ];
  }

  unmatchedReasonLabel(reason: string): string {
    if (reason === 'ambiguous_match') {
      return 'Ambiguous — multiple primary variants match';
    }
    if (reason === 'no_primary_match') {
      return 'No matching primary variant';
    }
    return reason;
  }

  closeMatchKindLabel(match: SquashCloseMatch): string {
    const kinds: string[] = [];
    if (match.partial_price_match) {
      kinds.push('Partial price match');
    }
    if (match.close_price_match) {
      kinds.push('Within 5%');
    }
    return kinds.join(' · ') || 'Close match';
  }

  closeMatchPriceDetail(match: SquashCloseMatch): string {
    const parts: string[] = [];
    if (match.matched_price_fields?.length) {
      parts.push(`Exact: ${match.matched_price_fields.join(', ')}`);
    }
    if (match.close_price_fields?.length) {
      parts.push(`~5%: ${match.close_price_fields.join(', ')}`);
    }
    if (match.mismatched_price_fields?.length) {
      parts.push(`Differ: ${match.mismatched_price_fields.join(', ')}`);
    }
    return parts.join(' · ');
  }
}
